var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useState } from "react";
import { useAnalyticsLogging } from "../../../../../Shared/hooks/useAnalyticsLogging/useAnalyticsLogging";
import { useLogging } from "../../../../../Shared/hooks/useLogging/useLogging";
import { BillingInformationContext } from "../../../BillingInformationContext";
import { usePaymentMethods } from "../../../../../SWR/Payments/usePaymentMethods";
import { initPaymentMethodCreation, logCreatePaymentMethodEvent } from "./PaymentMethodCreate-utils";
import { useCreatePaymentMethodMutation } from "../../../../../SWR/Payments/usePaymentMethodMutations";
/**
 * Backward-compatible hook for creating payment methods.
 *
 * ✅ Uses SWR mutations internally - cache is properly updated
 * ✅ Safe to use - no risk of falling out of sync
 *
 * This hook wraps `useCreatePaymentMethodMutation()` and provides:
 * - Loading and error state management
 * - Analytics logging
 * - Backward-compatible API for existing code
 *
 * For new code, you can use `useCreatePaymentMethodMutation()` directly if you prefer,
 * but this hook is fully supported and SWR-powered.
 */
export const usePaymentMethodCreate = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const { setClientSecret, setToken, setPublicKey, setPaymentProcessor } = useContext(BillingInformationContext);
    const { customerCreditCards } = usePaymentMethods();
    const { trigger: createMutation } = useCreatePaymentMethodMutation();
    const { logReactPixel, createKlaviyoEvent, gaEvent } = useAnalyticsLogging();
    const { datadogLog } = useLogging();
    // Wrapper around SWR mutation for backwards compatibility
    const createPaymentMethodWrapper = (body) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        setError(undefined);
        try {
            yield createMutation(body);
            // Log analytics event
            logCreatePaymentMethodEvent(logReactPixel, createKlaviyoEvent, gaEvent)();
            return { success: true };
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to create payment method.";
            setError(errorMessage);
            return { success: false, message: errorMessage };
        }
        finally {
            setLoading(false);
        }
    });
    return {
        initPaymentMethodCreation: initPaymentMethodCreation(setLoading, setError, setToken, setPublicKey, setClientSecret, setPaymentProcessor, datadogLog),
        createPaymentMethod: createPaymentMethodWrapper,
        loading,
        error,
    };
};
